import React, { useMemo } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { breakLabel, formatDuration, formatTime } from "../../../utils/attendance";
import { AGENT_INK, AGENT_LINE, AGENT_MUTED } from "../../../agentComponents/agentTheme";

// Today's shift drawn on a fixed hour scale, Keka's "day view" strip: green
// where the agent was working, amber where they were on a break, grey for the
// rest of the window. Reading a timeline is immediate in a way that a column
// of timestamps never is -- a long amber block is obvious at a glance.

const WORK = "#16A34A";
const BREAK = "#F59E0B";
const OVER = "#DC2626";

/** The hour window to draw: the rostered shift, widened to cover reality. */
const windowFor = (shifts, agent, now) => {
  const parseHour = (v) => {
    if (!/^\d{1,2}:\d{2}$/.test(v || "")) return null;
    const [h, m] = v.split(":").map(Number);
    return h + m / 60;
  };

  let from = parseHour(agent?.shiftStart);
  let to = parseHour(agent?.shiftEnd);
  if (from === null || to === null || to <= from) {
    from = 9;
    to = 18;
  }

  const asHours = (d) => {
    const x = new Date(d);
    return x.getHours() + x.getMinutes() / 60;
  };
  for (const s of shifts) {
    from = Math.min(from, asHours(s.clockInAt));
    to = Math.max(to, asHours(s.clockOutAt || now));
  }

  return { from: Math.max(0, Math.floor(from) - 1), to: Math.min(24, Math.ceil(to) + 1) };
};

export default function DayTimeline({ shifts, agent, now }) {
  const { segments, ticks } = useMemo(() => {
    const live = shifts.filter((s) => s.clockInAt);
    const win = windowFor(live, agent, now);
    const span = Math.max(1, win.to - win.from);

    const hourOf = (d) => {
      const x = new Date(d);
      return x.getHours() + x.getMinutes() / 60 + x.getSeconds() / 3600;
    };
    const pct = (d) => ((hourOf(d) - win.from) / span) * 100;

    const out = [];
    for (const s of live) {
      const start = pct(s.clockInAt);
      const end = pct(s.clockOutAt || now);
      out.push({
        kind: "work",
        left: start,
        width: Math.max(0.4, end - start),
        title: `Shift ${formatTime(s.clockInAt)} - ${s.clockOutAt ? formatTime(s.clockOutAt) : "now"}`,
      });

      for (const b of s.breaks || []) {
        const bStart = pct(b.startAt);
        const bEnd = pct(b.endAt || now);
        out.push({
          kind: b.overran ? "over" : "break",
          left: bStart,
          width: Math.max(0.4, bEnd - bStart),
          title: `${breakLabel(b)} · ${formatDuration(b.durationMs)}${b.overran ? " (over limit)" : ""}`,
        });
      }
    }

    const marks = [];
    const step = span > 12 ? 3 : span > 7 ? 2 : 1;
    for (let h = Math.ceil(win.from); h <= win.to; h += step) {
      marks.push({ hour: h, left: ((h - win.from) / span) * 100 });
    }

    return { segments: out, ticks: marks };
  }, [shifts, agent, now]);

  const label = (h) => {
    const hour = h % 24;
    const suffix = hour < 12 ? "AM" : "PM";
    const display = hour % 12 === 0 ? 12 : hour % 12;
    return `${display} ${suffix}`;
  };

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "16px", border: `1px solid ${AGENT_LINE}`, p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: AGENT_INK }}>Today's Timeline</Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          {[
            { c: WORK, l: "Working" },
            { c: BREAK, l: "Break" },
            { c: OVER, l: "Over limit" },
          ].map((x) => (
            <Box key={x.l} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "3px", bgcolor: x.c }} />
              <Typography sx={{ fontSize: 12, color: AGENT_MUTED }}>{x.l}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {!segments.length ? (
        <Box
          sx={{
            height: 34,
            borderRadius: "8px",
            bgcolor: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 12.5, color: AGENT_MUTED }}>
            Nothing logged yet today — clock in to start the timeline.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ position: "relative", height: 34, borderRadius: "8px", bgcolor: "#F1F5F9", overflow: "hidden" }}>
          {segments.map((seg, i) => (
            <Tooltip key={`${seg.kind}-${i}`} title={seg.title} arrow>
              <Box
                sx={{
                  position: "absolute",
                  top: seg.kind === "work" ? 0 : 6,
                  bottom: seg.kind === "work" ? 0 : 6,
                  left: `${seg.left}%`,
                  width: `${seg.width}%`,
                  bgcolor: seg.kind === "work" ? WORK : seg.kind === "over" ? OVER : BREAK,
                  // Breaks sit on top of the work band they interrupt.
                  borderRadius: seg.kind === "work" ? "6px" : "4px",
                  zIndex: seg.kind === "work" ? 1 : 2,
                  cursor: "pointer",
                  transition: "filter .15s",
                  "&:hover": { filter: "brightness(1.08)" },
                }}
              />
            </Tooltip>
          ))}
        </Box>
      )}

      <Box sx={{ position: "relative", height: 18, mt: 0.75 }}>
        {ticks.map((t) => (
          <Typography
            key={t.hour}
            sx={{
              position: "absolute",
              left: `${t.left}%`,
              transform: "translateX(-50%)",
              fontSize: 10.5,
              color: "#94A3B8",
              whiteSpace: "nowrap",
            }}
          >
            {label(t.hour)}
          </Typography>
        ))}
      </Box>
    </Box>
  );
}
